/**
 * Query ("Ask") contract — the dashboard's agentic-RAG surface (PR3).
 *
 * This is a STREAMING surface, not a JSON one: `POST /api/surfaces/query` responds with
 * `content-type: text/event-stream` and emits `data: {json}\n\n` frames. So there is NO surfaceEnvelope
 * here — `status` never applies, and `loading` is the stream itself, not a state.
 *
 * HAND-SYNCED to the MVP's copy (`MVP/lib/dashboard/contracts/query.ts`) — a contract edit is incomplete
 * until both copies move together. The bounds below mirror the BFF route's enforced Body.
 */
import { z } from "zod";

// The ACCEPTED bounds, not the prompt window. Named after the route's own constants
// (HISTORY_TURNS_ACCEPTED / HISTORY_CHARS_ACCEPTED) so they cannot be confused with the smaller trim
// window in the MVP's lib/query/memory.ts. Rejecting at the window is what killed long threads
// permanently: useAskQuery posts every completed turn uncapped, so at turn 21 a `.max(20)` 400s, the SPA
// renders an error turn, errored turns are excluded from history, the count never falls back under the
// cap, and that thread fails forever with no recovery. These ceilings are an amplifier guard only — far
// above any real conversation, there so a caller cannot post megabytes to be trimmed. 60,000 matches the
// transcript route's MAX_ANSWER_CHARS: a turn that can be PERSISTED must be postable.
const HISTORY_TURNS_ACCEPTED = 200;
const HISTORY_CHARS_ACCEPTED = 60_000;

// A NARROWING relevance filter (S4-2, spec 2026-07-31-query-spa-surface §4.1). It can only ever SHRINK an
// already-entitled result set: every read still joins query_entitled_ids server-side, and scope
// predicates are ANDed after that join. NO field here is an identity: `people` are display-name STRINGS
// resolved server-side against the roster (never a user_id).
//
// `sites` is z.string() on the wire, not an enum: QUERY_SITES lives in the MVP's lib/query/config.ts and
// must not gain a copy here — the ROUTE validates each value and 400s on an unknown one (rejected, never
// dropped: a silently ignored filter is a false scope). Facets the shipped engine cannot apply yet
// (until / people / projectIds / entryKinds, multi-site) are likewise 400d, per the facets endpoint's
// `capabilities` — the SPA must not offer a chip the capabilities object does not advertise.
export const QueryScopeSchema = z.object({
  sites: z.array(z.string().max(40)).max(12).nullish(),
  since: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(), // inclusive, UTC day
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(), // inclusive, UTC day
  people: z.array(z.string().max(120)).max(10).nullish(),
  projectIds: z.array(z.string().uuid()).max(10).nullish(),
  entryKinds: z.array(z.string().max(24)).max(8).nullish(),
});
export type QueryScope = z.infer<typeof QueryScopeSchema>;

// Request — the body contributes the QUESTION and its RELEVANCE SCOPE and nothing else. It NEVER carries
// a user_id, team_id, or visibility mode: the server derives the principal from the token.
export const QueryRequestSchema = z.object({
  question: z.string().min(1).max(2000),
  deep: z.boolean().nullish(),
  scope: QueryScopeSchema.nullish(),
  // The Ask chat this question belongs to (U6). Enables the chat-scoped searchAttachments tool for a
  // thread that has a ready upload. NOT a body-supplied identity — the server proves ownership of the id
  // in-query (bug-071 shape) before it reaches the engine and derives the principal from the token as
  // ever; a chat the caller does not own is a 404, never a scope widening.
  chatId: z.string().uuid().nullish(),
  history: z
    .array(
      z.object({
        question: z.string().max(HISTORY_CHARS_ACCEPTED),
        answer: z.string().max(HISTORY_CHARS_ACCEPTED),
      }),
    )
    .max(HISTORY_TURNS_ACCEPTED)
    .nullish(),
});
export type QueryRequest = z.infer<typeof QueryRequestSchema>;

// A cited source. `n` is the citation number the model was SHOWN and cites as [n] — it is NOT the array
// index (a note re-surfaced by a later parallel search keeps its first number). Resolve [n] through this
// field, never through position (bug-425). `scope` says which detail route the citation deep-links to —
// "team" → /conversations/:id, "private" → the caller's own /my-conversations/:id.
export const QuerySourceSchema = z.object({
  n: z.number(),
  id: z.string(),
  title: z.string().nullish(),
  site: z.string(),
  // Short preview of the source conversation (distilled summary, truncated) for the Sources panel.
  snippet: z.string().nullish(),
  // The note's CONTENT date, YYYY-MM-DD UTC — the source system's own last-updated time, never our ingest
  // time. Rendered on the source card so an answer's vintage is auditable without opening the note. The
  // same string the model is shown, so the card and the answer are dated from one clock. .nullish() —
  // Postgres NULL, and absent on turns persisted before this field existed (those cards render no date).
  // WARNING: this object has no .passthrough(). This copy is HAND-SYNCED with the MVP's
  // (lib/dashboard/contracts/query.ts) — widening one alone strips the field silently, with a green build
  // and a green typecheck in both repos. Symptom: dates show live, then vanish on thread reload.
  updated: z.string().nullish(),
  scope: z.enum(["team", "private"]),
  // THE REAL ARTIFACT this note was distilled from — the Slack message, the PR, the Doc, the Gmail
  // thread — derived server-side by the MVP's lib/memory/source-links.ts. A citation can click straight
  // through instead of landing on Terum's own paraphrase and needing a second click.
  //
  // NULL IS CORRECT AND COMMON, never a value to invent around: ai-chat sources (chatgpt / claude /
  // perplexity / claude-code) have no external home by design, a teammate's email note withholds its
  // link because a Gmail thread id is per-mailbox, a teammate's PRIVATE slack (tier-3 DM) note withholds
  // its permalink unless the caller is named in that note's audience, and any failed mirror read
  // degrades to null. In every case the note detail route (see `scope`) is the working destination —
  // treat `url` as an UPGRADE on that link, not a replacement for it. .nullish() also covers turns
  // persisted before this field existed.
  // WARNING (same as `updated`): no .passthrough() — this field exists on the wire ONLY because both
  // hand-synced copies carry it; removing it from one copy alone strips it silently, twice (SSE parse +
  // the chats-route re-validation), with a green build in both repos.
  url: z.string().nullish(),
  // Whether the finished answer actually CITES this source, as opposed to merely having retrieved it.
  // The frame always carries every source handed to synthesis — presentation is the client's call — but
  // a panel that renders 24 cards under an answer citing two of them overstates the evidence base.
  // .nullish(): absent on turns persisted before this field existed, and FALSE on the first of the two
  // sources frames (the answer does not exist yet). Treat absent as "unknown", not as "not cited".
  cited: z.boolean().nullish(),
});
export type QuerySource = z.infer<typeof QuerySourceSchema>;

// SSE frames. Frame order over the wire on a fresh answer:
//   `progress`* → `sources` → `token`* → `sources` → `done`
// An `error` frame may terminate the stream at any point and is TERMINAL (no `done` follows it). An
// `error` is a real failed turn — never render it as an empty answer bubble (bug-424).
//
// `sources` ARRIVES TWICE ON A FRESH ANSWER, and neither copy is terminal. The first lands BEFORE the
// first token so the reader can read source-first while the answer writes; every entry on it carries
// `cited: false`, because whether the answer used a source is not yet a fact. The second lands after
// synthesis with `cited` resolved. The SHAPE is identical — patch the turn's sources on every frame; a
// client that ignores `cited` simply re-applies the list it has. If synthesis fails, the second frame
// never arrives and the first one's `cited: false` is literally true. A CACHE HIT is the one other
// shape: `token` (the whole answer at once) → `sources` (once, `cited` already resolved) → `done`.
// Count frames for nothing; patch on each one.
//
// `progress` is ADVISORY and NON-TERMINAL: zero or more may arrive before the first token, and a turn is
// never "complete" because of one. It exists because the engine's search phase emits no text, leaving the
// client blank for ~8-11s. `stage:"searching"` arrives once with no detail (work started), then once per
// planner sub-query with `detail` set; `stage:"reading"` arrives once with `count` = the number of notes
// handed to synthesis. Ignoring the frame entirely is supported degraded behavior — which is what let the
// server ship it ahead of this client.
export const QueryStreamEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("token"), value: z.string() }),
  z.object({ type: z.literal("sources"), value: z.array(QuerySourceSchema) }),
  // `accessLogId` (S3-B1, MVP migration 156): the server-minted `query_access_log` parent id for THIS
  // answer. The SPA echoes it back verbatim on the transcript POST (the D31 "linkage echo") so the server
  // can link `query_chat_turns` to its access record — the server VERIFIES ownership before storing, so a
  // stale/foreign/absent echo degrades to an unlinked turn, never an error. `.nullish()` because the
  // server's flag may be off, the write may have failed, or the server may predate the field.
  z.object({ type: z.literal("done"), accessLogId: z.string().uuid().nullish() }),
  z.object({ type: z.literal("error"), message: z.string() }),
  // `stage:"skill"` (skills S1, Teddy directive 2026-08-12): a skill activation, `detail` = the skill
  // name — activation is visible, never silent. useAskQuery ACCUMULATES these per turn (deduped) and
  // the thinking indicator annotates with the active skills; a client that predates this value
  // safeParse-drops the whole frame (the documented ship-server-first degradation). Hand-synced with
  // the MVP copy (lib/dashboard/contracts/query.ts), same PR both repos.
  z.object({
    type: z.literal("progress"),
    stage: z.enum(["searching", "reading", "skill"]),
    detail: z.string().nullish(), // .nullish(), never .optional() — the null-handling invariant
    count: z.number().nullish(),
  }),
  // `scope` (S4-2): what the search was ACTUALLY restricted to. Emitted once per fresh answer, after the
  // search loop and before the first synthesis token; NON-TERMINAL and absent on a cache hit (the cache
  // key includes the pinned scope, so a hit is scope-correct by construction; chips are client state).
  //   pinned    — exactly what this client sent, echoed.
  //   inferred  — what the planner added on its own and the engine APPLIED: renders as a visually
  //               distinct removable "auto" chip (S4-3). Never includes a value a pinned filter overrode.
  //   relaxed   — facet names the engine's empty-retry dropped mid-run ("sites", "since").
  //   unresolved — pinned people that matched nobody — surfaced, never silently widened (null until the
  //               people facet ships).
  z.object({
    type: z.literal("scope"),
    pinned: QueryScopeSchema.nullish(),
    inferred: QueryScopeSchema.nullish(),
    relaxed: z.array(z.string()).nullish(),
    unresolved: z.array(z.string()).nullish(),
  }),
  // `uploads` (U9, upload spec §5.5): this chat has `inFlight` files still indexing, so their
  // content was NOT available to the answer now streaming. The SPA's honest empty-state signal
  // (D32's "still indexing" notice) — per-file detail comes from GET /api/surfaces/uploads
  // (uploads.ts), this frame only says the condition exists. NON-TERMINAL, at most once per
  // answer, before the first progress frame, only when the count is non-zero; never on a cache
  // hit (an in-flight posture bypasses the cache server-side, so a hit implies zero). Same
  // ship-server-first rule as `progress`: a client that predates it drops it via safeParse.
  z.object({ type: z.literal("uploads"), inFlight: z.number() }),
]);
export type QueryStreamEvent = z.infer<typeof QueryStreamEventSchema>;
